/**
 * MayaRejectionAnalysisDialog — Modal que pede à Maya para analisar
 * uma rejeição eSocial e exibe diagnóstico + ações corretivas.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  AlertCircle, CheckCircle2, ArrowRight, RefreshCw, Loader2,
  Lightbulb, Wrench,
} from 'lucide-react';
import MayaAvatar from '../maya/MayaAvatar';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PRIO_META = {
  alta:  { color: 'bg-red-100 text-red-700 border-red-200', label: 'Alta' },
  media: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Média' },
  baixa: { color: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Baixa' },
};

const MayaRejectionAnalysisDialog = ({ open, onOpenChange, eventoId, onRetransmit }) => {
  const [loading, setLoading] = useState(false);
  const [analise, setAnalise] = useState(null);

  const carregar = async () => {
    if (!eventoId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/esocial/eventos/${eventoId}/maya-analise`, auth(),
      );
      setAnalise(r.data?.analise || null);
    } catch (e) {
      setAnalise(null);
    } finally {
      setLoading(false);
    }
  };

  const solicitarAnalise = async (force = false) => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/esocial/eventos/${eventoId}/maya-analisar${force ? '?force=true' : ''}`,
        {}, { ...auth(), timeout: 90000 },
      );
      setAnalise(r.data);
      toast.success('Maya concluiu a análise');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Maya falhou em analisar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && eventoId) {
      carregar();
    }
  }, [open, eventoId]);  // eslint-disable-line

  const dialogVariant = loading
    ? 'thinking'
    : analise
      ? (analise.pode_retransmitir ? 'celebrating' : 'alert')
      : 'happy';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <MayaAvatar variant={dialogVariant} size="md" showStatusDot pulse={loading}
                        data-testid="maya-avatar-dialog-header" />
            <div className="flex flex-col">
              <span>Maya — Análise de Rejeição eSocial</span>
              <span className="text-xs font-normal text-slate-500">
                Especialista contábil · gov.br · S-1.3
              </span>
            </div>
          </DialogTitle>
          <DialogDescription>
            A Maya analisou o XML enviado e as ocorrências da gov.br, e propõe um plano de correção.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-sm text-slate-600">
            <MayaAvatar variant="thinking" size="lg" pulse showStatusDot
                        data-testid="maya-avatar-loading" />
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> A Maya está analisando…
            </span>
          </div>
        )}

        {!loading && !analise && (
          <div className="text-center py-8 space-y-3">
            <MayaAvatar variant="happy" size="lg" data-testid="maya-avatar-empty" />
            <Lightbulb className="h-6 w-6 mx-auto text-amber-500" />
            <p className="text-sm text-slate-600">
              Ainda não há análise para este evento. Solicite uma análise à Maya.
            </p>
            <Button onClick={() => solicitarAnalise(true)} data-testid="maya-trigger-analysis-btn">
              Analisar com a Maya
            </Button>
          </div>
        )}

        {!loading && analise && (
          <div className="space-y-4">
            {/* Diagnóstico */}
            <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-indigo-700 mb-1">
                Diagnóstico
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {analise.diagnostico}
              </p>
              {analise.explicacao_para_leigo && (
                <p className="mt-2 text-xs text-slate-500 italic flex items-start gap-1.5">
                  <Lightbulb className="h-3.5 w-3.5 mt-0.5 text-amber-500 shrink-0" />
                  <span>{analise.explicacao_para_leigo}</span>
                </p>
              )}
            </div>

            {/* Ocorrências traduzidas */}
            {analise.ocorrencias_traduzidas?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Ocorrências traduzidas
                </div>
                <div className="space-y-2">
                  {analise.ocorrencias_traduzidas.map((o, i) => (
                    <div key={i}
                         className="rounded-md border border-red-100 bg-red-50/40 p-2 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-red-600 text-white text-[10px]">
                            {o.codigo}
                          </Badge>
                          <span className="text-sm font-medium text-slate-800">
                            {o.descricao_curta}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          <b>Causa provável:</b> {o.causa_provavel}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ações corretivas */}
            {analise.acoes_corretivas?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Ações corretivas sugeridas ({analise.acoes_corretivas.length})
                </div>
                <div className="space-y-2">
                  {analise.acoes_corretivas.map((a, i) => {
                    const meta = PRIO_META[a.prioridade] || PRIO_META.media;
                    return (
                      <div key={i}
                           className="rounded-md border bg-white p-3 shadow-sm space-y-2"
                           data-testid={`maya-action-${i}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Wrench className="h-4 w-4 text-indigo-600" />
                          <span className="font-semibold text-sm text-slate-800">
                            {a.titulo}
                          </span>
                          <Badge variant="outline" className={`${meta.color} text-[10px] py-0`}>
                            {meta.label}
                          </Badge>
                        </div>
                        {a.passos?.length > 0 && (
                          <ol className="text-xs text-slate-600 space-y-1 list-decimal pl-5">
                            {a.passos.map((p, j) => <li key={j}>{p}</li>)}
                          </ol>
                        )}
                        {a.campo_alvo && (
                          <div className="text-[11px] flex items-center gap-1.5 pt-1 border-t">
                            <ArrowRight className="h-3 w-3 text-slate-400" />
                            <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                              {a.campo_alvo}
                            </code>
                            {a.valor_sugerido && (
                              <>
                                <span className="text-slate-400">→</span>
                                <code className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                                  {a.valor_sugerido}
                                </code>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Retransmissão */}
            {analise.pode_retransmitir && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                <div className="flex-1 text-xs text-emerald-800">
                  <b>Recomendação:</b> após aplicar as correções acima, retransmita o evento.
                </div>
                {onRetransmit && (
                  <Button size="sm" onClick={() => onRetransmit(eventoId)}
                          data-testid="maya-retransmit-btn"
                          className="bg-emerald-600 hover:bg-emerald-700">
                    Retransmitir
                  </Button>
                )}
              </div>
            )}

            <div className="text-[11px] text-slate-400 text-right pt-1">
              Analisado em {analise.analisado_em ? new Date(analise.analisado_em).toLocaleString('pt-BR') : '-'} ·
              modelo {analise.modelo}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {analise && (
            <Button variant="outline" onClick={() => solicitarAnalise(true)}
                    disabled={loading}
                    data-testid="maya-reanalyze-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refazer análise
            </Button>
          )}
          <Button variant="default" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MayaRejectionAnalysisDialog;
